import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Briefcase,
  Compass,
  GraduationCap,
  HeartHandshake,
  Rocket,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
  Users,
} from 'lucide-react';
import useInView from '../hooks/useInView';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">
    {children}
  </p>
);

const DarkPanel = ({ children, className = '', innerClassName = '' }) => (
  <div
    className={`rounded-[2rem] border border-white/10 bg-white/[0.035] backdrop-blur-2xl transition-all duration-300 hover:border-cyan-300/25 hover:bg-white/[0.055] ${className}`}
    style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 0 36px rgba(0,240,255,0.045)' }}
  >
    <div className={innerClassName}>{children}</div>
  </div>
);

const preparationSteps = [
  {
    icon: Sparkles,
    title: 'Develop the idea',
    text: 'Children can turn interests from clubs, Academy and challenges into a clearer project concept.',
  },
  {
    icon: BookOpen,
    title: 'Build a portfolio',
    text: 'Badges, creative work, challenge results and project notes can become visible preparation evidence.',
  },
  {
    icon: Users,
    title: 'Find a team',
    text: 'The ecosystem helps children practice teamwork, communication and shared responsibility before submission.',
  },
  {
    icon: GraduationCap,
    title: 'Prepare to present',
    text: 'Children can learn how to explain the problem, the idea, the audience, the first version and the next step.',
  },
];

const pathwayCards = [
  {
    icon: ShieldCheck,
    step: '01',
    title: 'Grow inside ROMIND KIDS',
    desc: 'The child participates in a safer ecosystem built around communication, creativity, clubs, Academy, challenges and rewards.',
  },
  {
    icon: Compass,
    step: '02',
    title: 'Prepare a project concept',
    desc: 'Ideas can be shaped into a proposal with a goal, audience, first version, team needs and development plan.',
  },
  {
    icon: BadgeCheck,
    step: '03',
    title: 'Submit after eligibility',
    desc: 'Active members with at least 6 months on ROMIND KIDS may submit according to their plan limits.',
  },
  {
    icon: Trophy,
    step: '04',
    title: 'ROMIND review and selection',
    desc: 'ROMIND reviews projects for potential, preparation, feasibility, safety, originality and ecosystem fit.',
  },
  {
    icon: Rocket,
    step: '05',
    title: 'MVP support for selected projects',
    desc: 'Selected projects may receive up to $25,000 in MVP funding support for a first version or proof of viability.',
  },
];

const planAccess = [
  { plan: 'Free / Trial', access: 'Not included', emphasis: false },
  { plan: 'Basic', access: '1 project submission per year after 6 months on the platform', emphasis: false },
  { plan: 'Plus', access: '2 project submissions per year after 6 months on the platform', emphasis: true },
  { plan: 'All-Inclusive', access: 'Up to 5 project submissions per year across different directions after 6 months on the platform', emphasis: true },
  { plan: 'Family', access: 'Up to 5 family project submissions per year, shared between children', emphasis: true },
];

const faqItems = [
  {
    q: 'What is ROMIND Venture Studio?',
    a: 'ROMIND Venture Studio is a future project-development layer inside the ROMIND KIDS ecosystem. It is designed to help children move from interests, skills and portfolio work into structured project concepts that can be reviewed by ROMIND.',
  },
  {
    q: 'When is it launching?',
    a: 'ROMIND Venture Studio is planned for June 2027. The first project submission window is planned to open in July 2027.',
  },
  {
    q: 'Who can submit a project?',
    a: 'Children will be able to submit projects after they have been active on ROMIND KIDS for at least 6 months and meet the platform activity requirements for submission.',
  },
  {
    q: 'What kind of projects can children submit?',
    a: 'Projects may come from creative, educational, social, technology, design, media, product or innovation directions connected to the child’s growth inside ROMIND KIDS.',
  },
  {
    q: 'What does up to $25,000 in MVP funding support mean?',
    a: 'Selected projects may receive up to $25,000 to help create an MVP, test proof of viability, or build a first working version of the idea. This support is connected to project development and ROMIND review.',
  },
  {
    q: 'Is funding guaranteed?',
    a: 'No. ROMIND reviews submitted projects and selects the ideas that show strong preparation, creativity, feasibility, responsible planning and ecosystem fit.',
  },
  {
    q: 'Do higher plans guarantee selection?',
    a: 'No. Paid plans may provide more submission opportunities, but every project still goes through ROMIND review. A plan can increase the number of applications, not buy a decision.',
  },
  {
    q: 'Do parents or legal guardians participate?',
    a: 'Yes. Any formal project-development terms are handled with parents or legal guardians. The child remains the creator, presenter and active participant in the project journey.',
  },
  {
    q: 'Does the child remain the author of the idea?',
    a: 'Yes. ROMIND Venture Studio is designed to support young creators, not remove them from their own ideas. The child remains central to the concept, presentation and development path.',
  },
  {
    q: 'What happens if an MVP is successful?',
    a: 'If an MVP demonstrates strong potential, ROMIND may consider the project for further development, additional support or future funding opportunities through appropriate agreements with parents or legal guardians.',
  },
];

const VentureStudio = () => (
  <main data-testid="venture-studio-page" className="bg-[#05050A] min-h-screen overflow-hidden">
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-16 left-1/4 w-96 h-96 bg-purple-600/12 rounded-full blur-[140px] pointer-events-none animate-nebula" />
      <div className="absolute bottom-6 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none animate-nebula" />
      <div
        className="absolute inset-0 pointer-events-none opacity-35"
        style={{
          background:
            'radial-gradient(circle at 15% 20%, rgba(157,76,221,0.18), transparent 28%), radial-gradient(circle at 80% 18%, rgba(0,240,255,0.12), transparent 28%), radial-gradient(circle at 50% 85%, rgba(255,0,127,0.12), transparent 34%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <FadeIn className="lg:col-span-7">
            <SectionLabel>ROMIND Venture Studio</SectionLabel>
            <h1 className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.03] mb-6">
              From screen time<br />
              <span className="gradient-text">to real project potential.</span>
            </h1>
            <p className="text-white/62 text-base sm:text-lg leading-relaxed max-w-2xl mb-8">
              ROMIND KIDS is being built as one safe social-creative ecosystem where children can communicate, create, learn, join clubs, complete challenges, earn rewards and build a visible record of effort. Venture Studio is the future layer of that journey: a path where prepared young creators can submit project ideas for ROMIND review.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="#plan-access"
                data-testid="venture-hero-plan-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/45 text-white hover:bg-purple-500 transition-all duration-200"
              >
                View plan access <ArrowRight size={14} />
              </a>
              <Link
                to="/parents"
                data-testid="venture-hero-parent-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                Parent overview <ArrowRight size={14} />
              </Link>
            </div>
          </FadeIn>

          <FadeIn delay={120} className="lg:col-span-5">
            <DarkPanel innerClassName="p-6 sm:p-8">
              <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-300/20 flex items-center justify-center mb-6">
                <Rocket size={24} className="text-cyan-300" />
              </div>
              <p className="text-xs font-outfit font-semibold text-white/40 uppercase tracking-widest mb-3">Future Project Fund</p>
              <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">
                Coming June 2027
              </h2>
              <p className="text-white/55 text-sm leading-relaxed mb-6">
                The first project submission window is planned to open in July 2027 for eligible active ROMIND KIDS members.
              </p>
              <div className="space-y-3">
                {[
                  '6+ months active participation required',
                  'Project submissions reviewed by ROMIND',
                  'Selected projects may receive up to $25,000 in MVP funding support',
                  'Parents or legal guardians are included in formal terms',
                ].map((item) => (
                  <div key={item} className="flex gap-3 text-sm text-white/58 leading-relaxed">
                    <Star size={14} className="text-purple-300 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </DarkPanel>
          </FadeIn>
        </div>
      </div>
    </section>

    <section className="relative py-20 bg-[#080714] overflow-hidden">
      <div className="absolute top-0 left-1/3 w-80 h-80 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>The ecosystem path</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight max-w-3xl">
            Venture Studio is not a separate contest. It is the next layer of the ROMIND KIDS journey.
          </h2>
          <p className="text-white/55 text-base leading-relaxed max-w-3xl mb-10">
            Children can use the platform before the submission window opens: develop ideas, complete clubs and challenges, build portfolio evidence, improve skills, find teammates and prepare a stronger presentation.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {preparationSteps.map((item, index) => (
            <FadeIn key={item.title} delay={index * 70}>
              <DarkPanel className="h-full" innerClassName="p-5 sm:p-6 h-full">
                <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                  <item.icon size={19} className="text-cyan-300" />
                </div>
                <h3 className="font-outfit font-bold text-white text-lg mb-2">{item.title}</h3>
                <p className="text-white/48 text-sm leading-relaxed">{item.text}</p>
              </DarkPanel>
            </FadeIn>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {pathwayCards.map((item, index) => (
            <FadeIn key={item.title} delay={index * 70}>
              <DarkPanel className="h-full" innerClassName="p-5 h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-purple-500/10 border border-purple-300/20 flex items-center justify-center">
                    <item.icon size={17} className="text-purple-300" />
                  </div>
                  <span className="text-[10px] font-outfit font-semibold text-white/28 tracking-[0.18em] uppercase">{item.step}</span>
                </div>
                <h3 className="font-outfit font-bold text-white text-base mb-2">{item.title}</h3>
                <p className="text-white/45 text-xs leading-relaxed">{item.desc}</p>
              </DarkPanel>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="relative py-20 overflow-hidden">
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-[130px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-8">
          <FadeIn className="lg:col-span-7">
            <DarkPanel innerClassName="p-6 sm:p-8 lg:p-9 h-full">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-300/20 flex items-center justify-center mb-5">
                <Briefcase size={21} className="text-cyan-300" />
              </div>
              <SectionLabel>ROMIND Project Fund</SectionLabel>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-5 tracking-tight">
                Up to $25,000 in MVP funding support for selected projects.
              </h2>
              <div className="space-y-4 text-white/58 text-sm sm:text-base leading-relaxed">
                <p>
                  Each Venture Studio direction will have a seasonal project fund. Approved projects inside a track may receive up to $25,000 to build an MVP, test proof of viability or create a first working version of the product.
                </p>
                <p>
                  If an MVP proves the idea has real potential, ROMIND may consider the project for a next round of support, further development or deeper participation inside the ROMIND ecosystem.
                </p>
                <p>
                  ROMIND may fund project development and may receive a share, participation right or exclusive opportunity to continue funding and developing selected projects through appropriate agreements with parents or legal guardians.
                </p>
              </div>
            </DarkPanel>
          </FadeIn>

          <FadeIn delay={100} className="lg:col-span-5">
            <DarkPanel innerClassName="p-6 sm:p-8 h-full">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-300/20 flex items-center justify-center mb-5">
                <HeartHandshake size={21} className="text-purple-300" />
              </div>
              <h3 className="font-outfit font-bold text-2xl text-white mb-4">The child remains the creator.</h3>
              <div className="space-y-4 text-white/56 text-sm sm:text-base leading-relaxed">
                <p>
                  Venture Studio is designed to support young creators, not remove them from their own ideas. The child remains the author, presenting participant and part of the project journey.
                </p>
                <p>
                  Parents or legal guardians are included wherever formal terms, funding support or future development agreements are required.
                </p>
                <p className="text-white/40 text-xs leading-relaxed">
                  Submission access creates an opportunity to be reviewed. It does not guarantee selection, funding or future development.
                </p>
              </div>
            </DarkPanel>
          </FadeIn>
        </div>
      </div>
    </section>

    <section id="plan-access" className="relative py-20 bg-[#080714] overflow-hidden scroll-mt-24">
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[130px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Plan access</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight max-w-3xl">
            Venture Studio strengthens paid plans without turning selection into pay-to-win.
          </h2>
          <p className="text-white/55 text-base leading-relaxed max-w-3xl mb-10">
            Plans can define how many project submissions a child or family may make. ROMIND still reviews every project through the same selection logic.
          </p>
        </FadeIn>

        <FadeIn delay={100}>
          <DarkPanel innerClassName="p-4 sm:p-5 lg:p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {planAccess.map((item) => (
                <div
                  key={item.plan}
                  data-testid={`venture-plan-${item.plan.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={`rounded-2xl border p-5 min-h-[180px] ${item.emphasis ? 'bg-purple-500/10 border-purple-300/20' : 'bg-white/[0.035] border-white/10'}`}
                >
                  <h3 className="font-outfit font-bold text-white text-lg mb-3">{item.plan}</h3>
                  <p className="text-white/52 text-sm leading-relaxed">{item.access}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-2xl bg-[#05050A]/55 border border-white/10 p-4">
              <p className="text-white/50 text-sm leading-relaxed">
                A plan gives a submission limit. Project funding is not automatic and is available only for selected projects after ROMIND review.
              </p>
            </div>
          </DarkPanel>
        </FadeIn>
      </div>
    </section>

    <section className="relative py-20 overflow-hidden">
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Parent FAQ</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight max-w-3xl">
            Clear answers for parents before the submission window opens.
          </h2>
          <p className="text-white/55 text-base leading-relaxed max-w-3xl mb-10">
            Venture Studio is a future opportunity layer, not a promise of automatic funding. The value begins earlier: children can prepare, learn, create, collaborate and build a stronger project path inside ROMIND KIDS.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {faqItems.map((item, index) => (
            <FadeIn key={item.q} delay={(index % 4) * 60}>
              <DarkPanel className="h-full" innerClassName="p-5 sm:p-6 h-full">
                <h3 className="font-outfit font-bold text-white text-base sm:text-lg mb-3">{item.q}</h3>
                <p className="text-white/52 text-sm leading-relaxed">{item.a}</p>
              </DarkPanel>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={160}>
          <div className="text-center mt-12">
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/academy"
                data-testid="venture-academy-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/45 text-white hover:bg-purple-500 transition-all duration-200"
              >
                Explore ROMIND Academy <ArrowRight size={14} />
              </Link>
              <Link
                to="/testing"
                data-testid="venture-testing-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                Join early testing <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default VentureStudio;
